import { forwardRef } from 'react';

import Tippy from '@tippyjs/react';

import { cn } from 'lib/helpers/utility';

import Icon from '../Icon/Icon';

import { getVariantClasses } from './lib/helpers';

/**
 * @type {import('react').ForwardRefExoticComponent<import('./lib/propTypes').TypographyProps & import('react').RefAttributes<HTMLElement>>}
 */

const Typography = forwardRef((props, ref) => {
  const {
    variant = 'span',
    size = 'medium',
    parentClassName,
    className,
    children,
    color = 'neutral-900',
    textDecoration = 'none',
    iconColor = 'neutral',
    weight = variant.startsWith('h') ? 'medium' : 'normal',
    icon,
    iconRight,
    tooltip,
    tooltipPlacement = 'bottom',
    tooltipColor = 'dark',
    required = false,
    iconIsStroke = false,
    iconSize,
    disabled = false,
    iconClassName = '',
    ...rest
  } = props;

  const baseClasses = getVariantClasses(variant, size);

  const Component = variant === 'display' ? 'h1' : variant;

  return (
    <div className={cn('flex cursor-[inherit] items-center gap-2', parentClassName)}>
      {icon && (
        <Tippy
          content={tooltip || ''}
          disabled={!tooltip}
          placement={tooltipPlacement}
          className={cn(tooltipColor === 'dark' && 'tippy-dark')}>
          <div className="flex items-center">
            <Icon
              icon={icon}
              color={disabled ? 'neutral' : iconColor}
              iconIsStroke={iconIsStroke}
              size={iconSize}
              className={iconClassName}
            />
          </div>
        </Tippy>
      )}
      <div className="flex min-w-0 items-center">
        <Component
          ref={ref}
          className={cn(
            'flex items-center gap-2',
            baseClasses,
            `font-${weight}`,
            textDecoration,
            disabled ? 'cursor-not-allowed text-neutral-400' : `text-${color}`,
            className
          )}
          {...rest}>
          {children}
        </Component>
        {required && <span className="text-danger-400">*</span>}
      </div>
      {iconRight && (
        <Tippy
          content={tooltip || ''}
          disabled={!tooltip}
          placement={tooltipPlacement}
          className={cn(tooltipColor === 'dark' && 'tippy-dark')}>
          <div className="flex items-center">
            <Icon
              icon={iconRight}
              color={disabled ? 'neutral' : iconColor}
              iconIsStroke={iconIsStroke}
              iconSize={iconSize}
              className={iconClassName}
            />
          </div>
        </Tippy>
      )}
    </div>
  );
});

Typography.displayName = 'Typography';

export default Typography;
